import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, MenuItem, Snackbar, TextField } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useSnackbar } from "../../hooks/useSnackbar";
import { Panel, FilterGrid, ListGrid, PrimaryButton, GhostButton } from "./ui";
import DateRangePresetButtons from "../../components/DateRangePresetButtons";
import { getDateRangePreset } from "../../utils/dateRangePresets";
import { formatEpoch } from "../../utils/dateUtils";
import {
  formatKurus,
  TRANSACTION_TYPES,
  transactionTypeLabel,
  balanceTypeLabel,
} from "./constants";
import { getTransactions } from "../../api/digitalPayAPI";
import {
  createAndDownloadExcel,
  generateExcelFilename,
} from "../../utils/excelExportUtils";
import { isSuperAdminUser } from "./roleUtils";

// İşlem geçmişi salt okunur. Backend liste ucunda filtre/sayfalama sunmuyor,
// o yüzden filtreleme istemcide.
// ponytail: kayıt sayısı büyürse backend'e start/end/count eklenmeli.
export default function TransactionsPanel({ lookups }) {
  const { firms = [] } = lookups;
  const canSelectFirm = isSuperAdminUser();
  const assignedFirmName = canSelectFirm ? "" : (firms[0]?.name ?? "");
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();

  const [draft, setDraft] = useState({
    start: "",
    end: "",
    firm: assignedFirmName,
    type: "",
    search: "",
  });
  // Uygulanan filtre; "Filtrele"ye basılana kadar liste değişmez.
  const [applied, setApplied] = useState({
    start: "",
    end: "",
    firm: assignedFirmName,
    type: "",
    search: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setAllRows(await getTransactions());
    } catch (e) {
      showSnackbar(e.message || "İşlemler alınamadı", "error");
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!canSelectFirm) {
      setDraft((current) => ({ ...current, firm: assignedFirmName }));
      setApplied((current) => ({ ...current, firm: assignedFirmName }));
    }
  }, [assignedFirmName, canSelectFirm]);

  const rows = useMemo(() => {
    const { start, end, firm, type, search } = applied;
    const startEpoch = start ? new Date(`${start}T00:00:00`).getTime() / 1000 : null;
    const endEpoch = end ? new Date(`${end}T23:59:59`).getTime() / 1000 : null;
    const q = search.trim().toLowerCase();

    return allRows.filter((r) => {
      if (startEpoch !== null && r.epoch < startEpoch) return false;
      if (endEpoch !== null && r.epoch > endEpoch) return false;
      if (firm && r.firmName !== firm) return false;
      if (type !== "" && Number(r.transactionTypeId) !== Number(type)) return false;
      if (q) {
        const hay = `${r.nameSurname} ${r.cardNo} ${r.productName} ${r.transactionId} ${transactionTypeLabel(r.transactionTypeId)} ${balanceTypeLabel(r.amountType)}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [allRows, applied]);

  const apply = (start = draft.start, end = draft.end) =>
    setApplied({ ...draft, start, end });

  const handlePreset = (key) => {
    const { start, end } = getDateRangePreset(key);
    setDraft({ ...draft, start, end });
    setApplied({ ...draft, start, end });
  };

  const columns = useMemo(
    () => [
      {
        field: "epoch",
        headerName: "Tarih",
        flex: 1,
        minWidth: 150,
        valueFormatter: (v) => formatEpoch(v),
      },
      { field: "transactionId", headerName: "İşlem No", flex: 1, minWidth: 120 },
      { field: "nameSurname", headerName: "Personel", flex: 1, minWidth: 140 },
      { field: "cardNo", headerName: "Kart No", flex: 1, minWidth: 120 },
      { field: "firmName", headerName: "Firma", flex: 1, minWidth: 120 },
      { field: "departmentName", headerName: "Departman", flex: 1, minWidth: 110 },
      { field: "productName", headerName: "Ürün", flex: 1, minWidth: 120 },
      {
        field: "amountType",
        headerName: "Bakiye Tipi",
        width: 115,
        valueFormatter: (value) => balanceTypeLabel(value),
      },
      {
        field: "amount",
        headerName: "Tutar",
        width: 105,
        valueFormatter: (v) => formatKurus(v),
      },
      {
        field: "postBalance",
        headerName: "Kalan",
        width: 105,
        valueFormatter: (v) => formatKurus(v),
      },
      {
        field: "transactionTypeId",
        headerName: "İşlem Tipi",
        minWidth: 175,
        flex: 1,
        valueFormatter: (value) => transactionTypeLabel(value),
      },
    ],
    []
  );

  // Grid'de gösterilen biçim Excel'e de aynen gitsin.
  const excelValueTransformer = (value, column) => {
    if (column.field === "epoch") return formatEpoch(value);
    if (column.field === "amount" || column.field === "postBalance") return formatKurus(value);
    if (column.field === "transactionTypeId") return transactionTypeLabel(value);
    if (column.field === "amountType") return balanceTypeLabel(value);
    return value;
  };

  const handleExport = () => {
    const result = createAndDownloadExcel(
      rows,
      columns,
      "İşlemler",
      generateExcelFilename("Vendai_Digital_Pay_Islemler"),
      { valueTransformer: excelValueTransformer }
    );
    if (result.success) showSnackbar(`İndirildi: ${result.fileName}`, "success");
    else showSnackbar(result.error || "Dışa aktarılamadı", "error");
  };

  return (
    <>
      <Panel
        title="Mali İşlem Kayıtları"
        sub={`${rows.length} işlem kaydı`}
        action={<DateRangePresetButtons onSelect={handlePreset} />}
      >
        <FilterGrid>
          <TextField
            label="Ara (personel, kart, ürün, işlem no)"
            value={draft.search}
            onChange={(e) => setDraft({ ...draft, search: e.target.value })}
            size="small"
            fullWidth
          />
          <TextField
            select
            label="Firma"
            value={draft.firm}
            onChange={(e) => setDraft({ ...draft, firm: e.target.value })}
            disabled={!canSelectFirm}
            size="small"
            fullWidth
          >
            {canSelectFirm && <MenuItem value="">Tümü</MenuItem>}
            {firms.map((f) => (
              <MenuItem key={f.idfirm} value={f.name}>
                {f.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Başlangıç Tarihi"
            type="date"
            value={draft.start}
            onChange={(e) => setDraft({ ...draft, start: e.target.value })}
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
          />
          <TextField
            select
            label="İşlem Tipi"
            value={draft.type}
            onChange={(e) => setDraft({ ...draft, type: e.target.value })}
            size="small"
            fullWidth
          >
            <MenuItem value="">Tüm İşlem Tipleri</MenuItem>
            {TRANSACTION_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Bitiş Tarihi"
            type="date"
            value={draft.end}
            onChange={(e) => setDraft({ ...draft, end: e.target.value })}
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
          />
        </FilterGrid>

        <Box sx={{ display: "flex", gap: 1.5, mt: 2.5, flexWrap: "wrap" }}>
          <PrimaryButton startIcon={<FilterAltIcon />} onClick={() => apply()}>
            Filtrele
          </PrimaryButton>
          <GhostButton startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Yenile
          </GhostButton>
          <GhostButton startIcon={<FileDownloadIcon />} onClick={handleExport}>
            Dışa Aktar
          </GhostButton>
        </Box>

        <Box sx={{ mt: 2.5 }}>
          <ListGrid
            rows={rows}
            columns={columns}
            getRowId={(r) => r.idtransaction}
            loading={loading}
            height={520}
          />
        </Box>
      </Panel>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
